import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  Rocket as RocketIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';

const palette = {
  primary: '#1F3A5F',
  secondary: '#2C8C8C',
  accent: '#D6A75B',
  bg: '#F6F4EF',
  bgDark: '#0F1E2E',
  bgCard: '#FFFFFF',
  textDark: '#1a1a2e',
  textMuted: '#5a6872',
  textLight: '#E0E0E0',
  border: '#e8e4de',
  sectionAlt: '#F0EDE6',
};

// Subtle paper/noise texture overlay (no external assets needed).
const paperTextureSx = {
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.03,
    backgroundImage:
      'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")',
    backgroundRepeat: 'repeat',
    backgroundSize: '256px 256px',
    pointerEvents: 'none',
    zIndex: 1,
  },
};

/* ── Scroll-triggered visibility hook ── */
function useVisibleOnScroll(threshold = 0.15) {
  // 預設 visible=true：避免 IntersectionObserver 未觸發時內容永遠空白
  // （原本會等捲入畫面才 fade-in，但搜尋引擎爬蟲、預覽工具、某些 viewport 計算問題會導致永遠 opacity=0）
  const [visible, setVisible] = useState(true);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // 仍然觀察以保留動畫效果，但已預設可見作為 fallback
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, visible];
}

const templateDefs = [
  { id: 'classic', accent: '#1976d2', image: '/images/landing/template-classic.webp' },
  { id: 'fashion', accent: '#c9a84c', image: '/images/landing/template-fashion.webp' },
  { id: 'handmade', accent: '#8b6f47', image: '/images/landing/template-handmade.webp' },
  { id: 'food', accent: '#d4503a', image: '/images/landing/template-food.webp' },
  { id: 'tech', accent: '#58a6ff', image: '/images/landing/template-tech.webp' },
];

const featureIcons = ['🚀', '📦', '🏪', '💳', '📋', '📊'];
const planCodes = ['free', 'basic', 'pro', 'enterprise'];
const planPricesMonthly = [0, 599, 1299, 2999];
const planPricesYearly = [0, 6999, 14999, 29999];
const planHighlight = [false, false, true, false];

function Landing() {
  const { t } = useTranslation('landing');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const templates = templateDefs.map((td) => ({
    ...td,
    name: t(`templates.${td.id}_name`),
    desc: t(`templates.${td.id}_desc`),
    tags: (() => { const v = t(`templates.${td.id}_tags`, { returnObjects: true, defaultValue: [] }); return Array.isArray(v) ? v : []; })(),
  }));

  const features = featureIcons.map((icon, idx) => ({
    icon,
    title: t(`features.feature_${idx + 1}_title`),
    desc: t(`features.feature_${idx + 1}_desc`),
    details: [
      t(`features.feature_${idx + 1}_detail_1`),
      t(`features.feature_${idx + 1}_detail_2`),
      t(`features.feature_${idx + 1}_detail_3`),
    ],
  }));

  const howItWorks = [1, 2, 3].map((n) => ({
    step: String(n).padStart(2, '0'),
    title: t(`how_it_works.step_${n}_title`),
    desc: t(`how_it_works.step_${n}_desc`),
  }));

  const [billingYearly, setBillingYearly] = useState(false);

  const plans = planCodes.map((code, idx) => {
    const monthlyPrice = planPricesMonthly[idx];
    const yearlyPrice = planPricesYearly[idx];
    const isYearly = billingYearly && code !== 'free';
    return {
      name: t(`pricing.${code}_plan`),
      code,
      price: isYearly ? yearlyPrice : monthlyPrice,
      period: code === 'free' ? t('pricing.free_period') : (isYearly ? t('pricing.per_year') : t('pricing.per_month')),
      desc: t(`pricing.${code}_desc`),
      items: t(`pricing.plan_${code}_items`, { returnObjects: true }) || [],
      highlight: planHighlight[idx],
      cta: t(`pricing.cta_${code}`),
      saving: isYearly ? Math.round((1 - yearlyPrice / (monthlyPrice * 12)) * 100) : 0,
    };
  });

  const faqs = Array.from({ length: 10 }, (_, i) => ({
    q: t(`faq.q${i + 1}`),
    a: t(`faq.a${i + 1}`),
  }));

  const [featRef, featVis] = useVisibleOnScroll();
  const [howRef, howVis] = useVisibleOnScroll();
  const [tplRef, tplVis] = useVisibleOnScroll();
  const [priceRef, priceVis] = useVisibleOnScroll();
  const [trustRef, trustVis] = useVisibleOnScroll();
  const [faqRef, faqVis] = useVisibleOnScroll();

  const handleGetStarted = (planCode) => {
    if (planCode === 'enterprise') {
      navigate('/contact?plan=enterprise');
    } else {
      navigate(`/apply?plan=${planCode}`);
    }
  };

  const handleGetStartedWithTemplate = (planCode, templateId) => {
    if (planCode === 'enterprise') {
      navigate('/contact?plan=enterprise');
      return;
    }
    const tpl = String(templateId || '').toLowerCase();
    navigate(`/apply?plan=${planCode}&template=${encodeURIComponent(tpl)}`);
  };

  return (
    <Box
      sx={{
        bgcolor: palette.bg,
        '@keyframes fadeInUp': {
          from: { opacity: 0, transform: 'translateY(30px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '@keyframes fadeInScale': {
          from: { opacity: 0, transform: 'scale(0.92)' },
          to: { opacity: 1, transform: 'scale(1)' },
        },
        '@keyframes float': {
          '0%, 100%': { transform: 'translateY(0)' },
          '50%': { transform: 'translateY(-12px)' },
        },
        '@keyframes pulse': {
          '0%, 100%': { boxShadow: '0 0 0 0 rgba(214,167,91,0.4)' },
          '50%': { boxShadow: '0 0 0 12px rgba(214,167,91,0)' },
        },
        '@keyframes dotFloat': {
          '0%, 100%': { transform: 'translateY(0) rotate(0deg)', opacity: 0.5 },
          '50%': { transform: 'translateY(-20px) rotate(180deg)', opacity: 1 },
        },
      }}
    >
      {/* ── Navbar ── */}
      <Box
        sx={{
          bgcolor: 'rgba(255,255,255,0.97)',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${palette.border}`,
          py: { xs: 1, md: 1.2 },
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          boxShadow: '0 1px 8px rgba(0,0,0,0.04)',
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            {/* Logo */}
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{
                cursor: 'pointer',
                transition: 'transform 0.2s ease',
                '&:hover': { transform: 'scale(1.03)' },
              }}
              onClick={() => navigate('/landing')}
            >
              <Box
                component="img"
                src="/images/landing/logo-icon.png"
                alt="URStore"
                sx={{
                  height: { xs: 36, md: 42 },
                  width: 'auto',
                  filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.08))',
                }}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              <Box>
                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: { xs: '1.1rem', md: '1.3rem' },
                    letterSpacing: '-0.02em',
                    color: palette.primary,
                    lineHeight: 1.1,
                  }}
                >
                  URStore
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    color: palette.accent,
                    letterSpacing: '0.12em',
                    lineHeight: 1,
                    display: { xs: 'none', sm: 'block' },
                  }}
                >
                  OPEN YOUR STORE
                </Typography>
              </Box>
            </Stack>

            {/* Nav Links + CTA */}
            <Stack direction="row" spacing={{ xs: 0.5, md: 1.5 }} alignItems="center">
              {!isMobile && (
                <>
                  {[
                    { label: t('landing_nav.features'), action: () => navigate('/features') },
                    { label: t('landing_nav.templates'), action: () => document.getElementById('templates')?.scrollIntoView({ behavior: 'smooth' }) },
                    { label: t('landing_nav.pricing'), action: () => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }) },
                  ].map((nav) => (
                    <Button
                      key={nav.label}
                      size="small"
                      sx={{
                        color: palette.textMuted,
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        px: 1.5,
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 4,
                          left: '50%',
                          width: 0,
                          height: 2,
                          bgcolor: palette.accent,
                          borderRadius: 1,
                          transition: 'all 0.25s ease',
                          transform: 'translateX(-50%)',
                        },
                        '&:hover::after': { width: '60%' },
                        '&:hover': { color: palette.textDark, bgcolor: 'transparent' },
                      }}
                      onClick={nav.action}
                    >
                      {nav.label}
                    </Button>
                  ))}
                </>
              )}
              <Button
                variant="contained"
                size="small"
                onClick={() => handleGetStarted('free')}
                sx={{
                  bgcolor: palette.accent,
                  color: palette.bgDark,
                  fontWeight: 900,
                  borderRadius: 2,
                  px: { xs: 2, md: 3 },
                  py: 0.8,
                  fontSize: '0.85rem',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#c4963e', boxShadow: '0 2px 12px rgba(214, 167, 91, 0.3)' },
                }}
              >
                {t('landing_nav.free_trial')}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* ── Hero ── */}
      <Box
        sx={{
          bgcolor: palette.bgDark,
          color: 'white',
          py: { xs: 8, md: 11 },
          position: 'relative',
          overflow: 'hidden',
          ...paperTextureSx,
          // Dot pattern decoration
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
            pointerEvents: 'none',
            zIndex: 1,
          },
        }}
      >
        <Box sx={{ position: 'absolute', top: -120, right: -120, width: 420, height: 420, borderRadius: '50%', border: '1px solid rgba(44,140,140,0.18)' }} />
        <Box sx={{ position: 'absolute', bottom: -80, left: -80, width: 320, height: 320, borderRadius: '50%', border: '1px solid rgba(214,167,91,0.14)' }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip
                label={t('hero.trial_badge')}
                size="small"
                sx={{
                  mb: 2,
                  bgcolor: 'rgba(214,167,91,0.14)',
                  color: palette.accent,
                  fontWeight: 800,
                  border: '1px solid rgba(214,167,91,0.28)',
                  animation: 'fadeInUp 0.6s ease forwards',
                  opacity: 0,
                }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 950,
                  fontSize: { xs: '2.2rem', md: '3.15rem' },
                  lineHeight: 1.15,
                  letterSpacing: '-0.03em',
                  mb: 2,
                  animation: 'fadeInUp 0.7s ease 0.15s forwards',
                  opacity: 0,
                }}
              >
                {t('hero.title')}
                <br />
                <Box component="span" sx={{ color: palette.accent }}>
                  {t('hero.title_highlight')}
                </Box>
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: palette.textLight,
                  opacity: 0,
                  lineHeight: 1.7,
                  fontWeight: 400,
                  mb: 4,
                  animation: 'fadeInUp 0.7s ease 0.3s forwards',
                }}
              >
                {t('hero.subtitle')}
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ animation: 'fadeInUp 0.7s ease 0.45s forwards', opacity: 0 }}
              >
                <Button
                  size="large"
                  variant="contained"
                  onClick={() => handleGetStarted('free')}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    bgcolor: palette.accent,
                    color: palette.bgDark,
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 900,
                    boxShadow: '0 10px 30px rgba(214,167,91,0.25)',
                    '&:hover': { bgcolor: '#c4963e', boxShadow: '0 14px 36px rgba(214,167,91,0.28)' },
                  }}
                >
                  {t('hero.cta')}
                </Button>
                <Button
                  size="large"
                  variant="outlined"
                  onClick={() => navigate('/features')}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.28)',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 800,
                    '&:hover': { borderColor: 'rgba(255,255,255,0.48)', bgcolor: 'rgba(255,255,255,0.06)' },
                  }}
                >
                  {t('hero.cta_secondary')}
                </Button>
              </Stack>

              {/* Stats glass bar */}
              <Box
                sx={{
                  mt: 5,
                  display: 'inline-flex',
                  gap: 3,
                  flexWrap: 'wrap',
                  backdropFilter: 'blur(12px)',
                  bgcolor: 'rgba(255,255,255,0.06)',
                  borderLeft: `3px solid ${palette.accent}`,
                  borderRadius: 2,
                  px: 3,
                  py: 2,
                  animation: 'fadeInUp 0.7s ease 0.6s forwards',
                  opacity: 0,
                }}
              >
                {[
                  { k: t('hero.stat_setup_time'), v: t('hero.stat_setup_label') },
                  { k: t('hero.stat_import'), v: t('hero.stat_import_label') },
                  { k: t('hero.stat_cvs'), v: t('hero.stat_cvs_label') },
                  { k: t('hero.stat_free'), v: t('hero.stat_free_label') },
                ].map((s) => (
                  <Box key={s.k} sx={{ minWidth: 80 }}>
                    <Typography sx={{ color: palette.accent, fontWeight: 900, fontSize: { xs: '1.2rem', md: '1.35rem' } }}>
                      {s.k}
                    </Typography>
                    <Typography variant="caption" sx={{ color: palette.textLight, opacity: 0.7 }}>
                      {s.v}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              {/* Desktop: browser mockup with float + glow */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: '1px solid rgba(255,255,255,0.08)',
                    boxShadow: '0 30px 90px rgba(0,0,0,0.55), 0 0 60px rgba(214,167,91,0.08)',
                    animation: 'float 6s ease-in-out infinite, fadeInScale 0.8s ease 0.3s forwards',
                    opacity: 0,
                  }}
                >
                  <Box sx={{ bgcolor: '#f0f0f0', px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 0.8, borderBottom: '1px solid #e2e2e2' }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ff5f57' }} />
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#febc2e' }} />
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#28c840' }} />
                    <Box sx={{ flex: 1, ml: 1, bgcolor: '#fff', border: '1px solid #e8e8e8', borderRadius: 1, px: 1.3, py: 0.35, fontSize: '0.75rem', color: '#9aa0a6' }}>
                      mystore.urstore.tw
                    </Box>
                  </Box>
                  <Box
                    component="img"
                    src="/images/landing/hero-dashboard.webp"
                    alt="URStore 商店後台"
                    sx={{ width: '100%', display: 'block' }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </Card>
              </Box>
              {/* Mobile: hero image */}
              <Box sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center', mt: 4 }}>
                <Box
                  component="img"
                  src="/images/landing/hero-mobile.webp"
                  alt="URStore 行動版"
                  sx={{
                    maxWidth: '80%',
                    borderRadius: 3,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Features ── */}
      <Container ref={featRef} maxWidth="lg" sx={{ py: { xs: 7, md: 9 } }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h3" sx={{ fontWeight: 950, color: palette.textDark, mb: 1 }}>
            {t('features.title')}
          </Typography>
          {/* Gold underline */}
          <Box sx={{ width: 48, height: 3, bgcolor: palette.accent, borderRadius: 2, mx: 'auto', mb: 1.5 }} />
          <Typography variant="h6" sx={{ color: palette.textMuted, fontWeight: 400 }}>
            {t('features.subtitle')}
          </Typography>
        </Box>

        <Grid container spacing={2.5}>
          {features.map((f, idx) => (
            <Grid item xs={12} sm={6} md={4} key={f.title}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  bgcolor: palette.bgCard,
                  border: `1px solid ${palette.border}`,
                  boxShadow: '0 10px 26px rgba(0,0,0,0.04)',
                  borderBottom: `3px solid transparent`,
                  transition: 'all 0.3s ease',
                  opacity: featVis ? 1 : 0,
                  transform: featVis ? 'translateY(0)' : 'translateY(30px)',
                  transitionDelay: `${idx * 0.08}s`,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 16px 40px rgba(0,0,0,0.08)',
                    borderBottom: `3px solid ${palette.accent}`,
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  {/* Emoji in rounded square */}
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2.5,
                      bgcolor: `${palette.accent}14`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      mb: 1.5,
                    }}
                  >
                    {f.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 900, color: palette.textDark, mb: 1, fontSize: '1.05rem' }}>
                    {f.title}
                  </Typography>
                  <Typography sx={{ color: palette.textMuted, lineHeight: 1.7, mb: 1.5 }}>
                    {f.desc}
                  </Typography>
                  {f.details && (
                    <Stack spacing={0.5}>
                      {f.details.map((d) => (
                        <Stack key={d} direction="row" spacing={0.8} alignItems="center">
                          <CheckIcon sx={{ fontSize: 14, color: palette.secondary, flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ color: palette.textMuted, fontSize: '0.82rem' }}>
                            {d}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ textAlign: 'center', mt: 5 }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/features')}
            endIcon={<ArrowForwardIcon />}
            sx={{
              bgcolor: palette.primary,
              fontWeight: 900,
              borderRadius: 2,
              px: 4,
              py: 1.4,
              '&:hover': { bgcolor: '#152b46' },
            }}
          >
            {t('features.view_all_features')}
          </Button>
        </Box>
      </Container>

      {/* ── Social Proof / Use Cases ── */}
      <Box sx={{ bgcolor: palette.sectionAlt, py: { xs: 6, md: 8 }, ...paperTextureSx }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography variant="h3" sx={{ fontWeight: 950, color: palette.textDark, mb: 1 }}>
              {t('social_proof.title')}
            </Typography>
            <Box sx={{ width: 48, height: 3, bgcolor: palette.accent, borderRadius: 2, mx: 'auto', mb: 1.5 }} />
            <Typography variant="h6" sx={{ color: palette.textMuted, fontWeight: 400 }}>
              {t('social_proof.subtitle')}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {[
              {
                role: t('social_proof.seller_1_role'),
                scenario: t('social_proof.seller_1_scenario'),
                quote: t('social_proof.seller_1_quote', { defaultValue: '在蝦皮競爭太激烈，價格戰打不完。用 URStore 開了自己的官網，把老客戶導過來，利潤提高不少。' }),
                highlight: t('social_proof.seller_1_highlight'),
                color: palette.secondary,
              },
              {
                role: t('social_proof.seller_2_role'),
                scenario: t('social_proof.seller_2_scenario'),
                quote: t('social_proof.seller_2_quote', { defaultValue: '以前都用 Google 表單收單，對帳超麻煩。現在用 URStore 開店，客戶自己下單付款，我只要負責出貨就好。' }),
                highlight: t('social_proof.seller_2_highlight'),
                color: palette.accent,
              },
              {
                role: t('social_proof.seller_3_role'),
                scenario: t('social_proof.seller_3_scenario'),
                quote: t('social_proof.seller_3_quote', { defaultValue: '手作文創模板很有質感，放上作品照片和品牌故事，客戶看了就想下單。比在社群平台賣東西專業多了。' }),
                highlight: t('social_proof.seller_3_highlight'),
                color: '#8b6f47',
              },
            ].map((item, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    bgcolor: palette.bgCard,
                    border: `1px solid ${palette.border}`,
                    borderTop: `3px solid ${item.color}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 32px rgba(0,0,0,0.08)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                      <Chip
                        label={item.role}
                        size="small"
                        sx={{ fontWeight: 800, bgcolor: `${item.color}15`, color: item.color, border: `1px solid ${item.color}30` }}
                      />
                      <Typography variant="caption" sx={{ color: palette.textMuted }}>
                        {item.scenario}
                      </Typography>
                    </Stack>
                    <Typography sx={{ color: palette.textDark, lineHeight: 1.8, mb: 2, fontStyle: 'italic', fontSize: '0.95rem' }}>
                      「{item.quote}」
                    </Typography>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        bgcolor: `${item.color}10`,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1.5,
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 14, color: item.color }} />
                      <Typography variant="caption" sx={{ color: item.color, fontWeight: 700 }}>
                        {item.highlight}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── How it works ── */}
      <Box ref={howRef} sx={{ bgcolor: palette.sectionAlt, py: { xs: 7, md: 9 }, ...paperTextureSx }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography variant="h3" sx={{ fontWeight: 950, color: palette.textDark, mb: 1 }}>
              {t('how_it_works.title')}
            </Typography>
            <Box sx={{ width: 48, height: 3, bgcolor: palette.accent, borderRadius: 2, mx: 'auto', mb: 1.5 }} />
            <Typography variant="h6" sx={{ color: palette.textMuted, fontWeight: 400 }}>
              {t('how_it_works.subtitle')}
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center" sx={{ position: 'relative' }}>
            {/* Dashed connector line (desktop only) */}
            {!isMobile && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 56,
                  left: '22%',
                  right: '22%',
                  height: 0,
                  borderTop: `2px dashed ${palette.accent}44`,
                  zIndex: 0,
                }}
              />
            )}
            {howItWorks.map((h, idx) => (
              <Grid item xs={12} md={4} key={h.step}>
                <Box
                  sx={{
                    textAlign: 'center',
                    position: 'relative',
                    zIndex: 1,
                    opacity: howVis ? 1 : 0,
                    transform: howVis ? 'translateY(0)' : 'translateY(30px)',
                    transition: `all 0.5s ease ${idx * 0.15}s`,
                  }}
                >
                  {/* Circle number with outer ring */}
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      border: `3px solid ${palette.accent}33`,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      boxShadow: `0 8px 24px ${palette.accent}22`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        bgcolor: palette.accent,
                        color: palette.bgDark,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 950,
                        fontSize: '1.3rem',
                      }}
                    >
                      {h.step}
                    </Box>
                  </Box>
                  {/* Card container for text */}
                  <Box
                    sx={{
                      bgcolor: palette.bgCard,
                      borderRadius: 3,
                      p: 2.5,
                      border: `1px solid ${palette.border}`,
                      boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                    }}
                  >
                    <Typography sx={{ fontWeight: 950, color: palette.textDark, fontSize: '1.2rem', mb: 1 }}>
                      {h.title}
                    </Typography>
                    <Typography sx={{ color: palette.textMuted, lineHeight: 1.7 }}>
                      {h.desc}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => handleGetStarted('free')}
              endIcon={<ArrowForwardIcon />}
              sx={{
                bgcolor: palette.accent,
                color: palette.bgDark,
                fontWeight: 950,
                borderRadius: 2,
                px: 4,
                py: 1.4,
                boxShadow: '0 10px 30px rgba(214,167,91,0.25)',
                '&:hover': { bgcolor: '#c4963e' },
              }}
            >
              {t('how_it_works.start_now')}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ── Templates ── */}
      <Box id="templates" ref={tplRef} sx={{ bgcolor: palette.bg, py: { xs: 7, md: 9 } }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography variant="h3" sx={{ fontWeight: 950, color: palette.textDark, mb: 1 }}>
              {t('templates.title')}
            </Typography>
            <Box sx={{ width: 48, height: 3, bgcolor: palette.accent, borderRadius: 2, mx: 'auto', mb: 1.5 }} />
            <Typography variant="h6" sx={{ color: palette.textMuted, fontWeight: 400 }}>
              {t('templates.subtitle')}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {templates.map((tpl, idx) => (
              <Grid item xs={12} sm={6} md={4} key={tpl.id}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    bgcolor: palette.bgCard,
                    border: `1px solid ${palette.border}`,
                    borderTop: `4px solid ${tpl.accent}`,
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    opacity: tplVis ? 1 : 0,
                    transform: tplVis ? 'translateY(0)' : 'translateY(30px)',
                    transitionDelay: `${idx * 0.08}s`,
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 16px 40px rgba(0,0,0,0.08)',
                    },
                    '&:hover .tpl-image': {
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <Box sx={{ overflow: 'hidden' }}>
                    <Box
                      component="img"
                      className="tpl-image"
                      src={tpl.image}
                      alt={tpl.name}
                      loading="lazy"
                      sx={{
                        width: '100%',
                        height: 220,
                        objectFit: 'cover',
                        bgcolor: '#eee',
                        transition: 'transform 0.4s ease',
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography sx={{ fontWeight: 950, color: palette.textDark }}>
                        {tpl.name}
                      </Typography>
                      <Chip
                        size="small"
                        label={t('templates.previewable')}
                        icon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                        sx={{ bgcolor: `${tpl.accent}18`, color: tpl.accent, border: `1px solid ${tpl.accent}30` }}
                      />
                    </Stack>
                    <Typography sx={{ color: palette.textMuted, lineHeight: 1.7, mb: 2 }}>
                      {tpl.desc}
                    </Typography>
                    <Stack direction="row" spacing={0.8} sx={{ flexWrap: 'wrap', gap: 0.8 }}>
                      {(tpl.tags || []).map((tag) => (
                        <Chip
                          key={tag}
                          size="small"
                          label={tag}
                          sx={{
                            bgcolor: `${tpl.accent}10`,
                            color: tpl.accent,
                            border: `1px solid ${tpl.accent}25`,
                            fontWeight: 600,
                          }}
                        />
                      ))}
                    </Stack>
                  </CardContent>
                  <CardActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => navigate(`/preview/${tpl.id}`)}
                      sx={{ borderRadius: 2, fontWeight: 900, borderColor: palette.border, color: palette.primary, '&:hover': { borderColor: palette.primary, bgcolor: 'rgba(31,58,95,0.04)' } }}
                    >
                      {t('templates.preview')}
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => handleGetStartedWithTemplate('free', tpl.id)}
                      sx={{ borderRadius: 2, fontWeight: 900, bgcolor: tpl.accent, color: palette.bgDark, '&:hover': { bgcolor: tpl.accent, filter: 'brightness(0.95)' } }}
                    >
                      {t('templates.use_template')}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Pricing ── */}
      <Box id="pricing" ref={priceRef} sx={{ py: { xs: 7, md: 9 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography variant="h3" sx={{ fontWeight: 950, color: palette.textDark, mb: 1 }}>
              {t('pricing.title')}
            </Typography>
            <Box sx={{ width: 48, height: 3, bgcolor: palette.accent, borderRadius: 2, mx: 'auto', mb: 1.5 }} />
            <Typography variant="h6" sx={{ color: palette.textMuted, fontWeight: 400 }}>
              {t('pricing.subtitle')}
            </Typography>
            {/* Zero commission badge */}
            <Chip
              label={t('pricing.zero_commission')}
              sx={{ mt: 2, bgcolor: palette.secondary, color: 'white', fontWeight: 900, fontSize: '0.9rem', px: 2, py: 0.5 }}
            />
            {/* Billing toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mt: 3 }}>
              <Typography sx={{ fontWeight: billingYearly ? 400 : 700, color: billingYearly ? palette.textMuted : palette.textDark, cursor: 'pointer' }} onClick={() => setBillingYearly(false)}>
                {t('pricing.monthly')}
              </Typography>
              <Box
                onClick={() => setBillingYearly(!billingYearly)}
                sx={{
                  width: 52, height: 28, borderRadius: 14, bgcolor: billingYearly ? palette.secondary : palette.border,
                  position: 'relative', cursor: 'pointer', transition: 'all 0.3s',
                }}
              >
                <Box sx={{
                  width: 22, height: 22, borderRadius: '50%', bgcolor: 'white', position: 'absolute', top: 3,
                  left: billingYearly ? 27 : 3, transition: 'left 0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </Box>
              <Typography sx={{ fontWeight: billingYearly ? 700 : 400, color: billingYearly ? palette.textDark : palette.textMuted, cursor: 'pointer' }} onClick={() => setBillingYearly(true)}>
                {t('pricing.yearly')}
              </Typography>
              {billingYearly && (
                <Chip label={t('pricing.yearly_discount')} size="small" sx={{ bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 700 }} />
              )}
            </Box>
          </Box>

          <Grid container spacing={3} justifyContent="center">
            {plans.map((p, idx) => (
              <Grid item xs={12} sm={6} md={3} key={p.code}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    bgcolor: p.highlight ? '#FFFBF3' : palette.bgCard,
                    border: p.highlight ? `2px solid ${palette.accent}` : `1px solid ${palette.border}`,
                    boxShadow: p.highlight ? '0 16px 50px rgba(214,167,91,0.18)' : '0 10px 26px rgba(0,0,0,0.04)',
                    position: 'relative',
                    transform: p.highlight ? 'scale(1.04)' : 'none',
                    transition: 'all 0.3s ease',
                    opacity: priceVis ? 1 : 0,
                    transitionDelay: `${idx * 0.08}s`,
                    zIndex: p.highlight ? 2 : 1,
                    '&:hover': {
                      transform: p.highlight ? 'scale(1.04) translateY(-4px)' : 'translateY(-4px)',
                      boxShadow: p.highlight
                        ? '0 20px 60px rgba(214,167,91,0.22)'
                        : '0 16px 40px rgba(0,0,0,0.08)',
                    },
                  }}
                >
                  {p.highlight && (
                    <Chip
                      label={t('pricing.popular')}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -14,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        bgcolor: palette.accent,
                        color: 'white',
                        fontWeight: 900,
                        px: 1.5,
                        fontSize: '0.82rem',
                        animation: 'pulse 2s ease-in-out infinite',
                      }}
                    />
                  )}
                  <CardContent sx={{ p: 3 }}>
                    <Typography sx={{ fontWeight: 950, color: palette.textDark, mb: 0.5 }}>
                      {p.name}
                    </Typography>
                    <Typography sx={{ color: palette.textMuted, mb: 2, lineHeight: 1.7 }}>
                      {p.desc}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                        <Typography component="span" sx={{ fontWeight: 700, color: palette.textMuted, fontSize: '0.95rem' }}>
                          NT$
                        </Typography>
                        <Typography component="span" sx={{ fontWeight: 950, color: palette.primary, fontSize: '2.4rem', lineHeight: 1 }}>
                          {p.price.toLocaleString()}
                        </Typography>
                        <Typography component="span" sx={{ color: palette.textMuted, ml: 0.5, fontSize: '0.9rem' }}>
                          /{p.period}
                        </Typography>
                      </Box>
                      {p.saving > 0 && (
                        <Typography sx={{ color: palette.secondary, fontWeight: 700, fontSize: '0.82rem', mt: 0.5 }}>
                          {t('pricing.save_percent', { percent: p.saving })}
                        </Typography>
                      )}
                    </Box>
                    <Divider sx={{ my: 2, borderColor: palette.border }} />
                    <Stack spacing={1}>
                      {p.items.map((item, i) => (
                        <Stack
                          key={item}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{
                            py: 0.5,
                            px: 1,
                            borderRadius: 1,
                            bgcolor: i % 2 === 0 ? 'rgba(0,0,0,0.015)' : 'transparent',
                          }}
                        >
                          <CheckIcon sx={{ fontSize: 18, color: palette.secondary }} />
                          <Typography variant="body2" sx={{ color: palette.textDark }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </CardContent>
                  <CardActions sx={{ p: 3, pt: 0 }}>
                    <Button
                      fullWidth
                      size="large"
                      variant={p.highlight ? 'contained' : 'outlined'}
                      onClick={() => handleGetStarted(p.code)}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 950,
                        py: 1.2,
                        ...(p.highlight
                          ? { bgcolor: palette.accent, '&:hover': { bgcolor: '#c4963e' } }
                          : { borderColor: palette.border, color: palette.primary, '&:hover': { borderColor: palette.primary, bgcolor: 'rgba(31,58,95,0.04)' } }),
                      }}
                    >
                      {p.cta}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Trust numbers ── */}
      <Box
        ref={trustRef}
        sx={{
          bgcolor: palette.bgDark,
          color: 'white',
          py: { xs: 5, md: 7 },
          position: 'relative',
          overflow: 'hidden',
          ...paperTextureSx,
          // Top gradient line
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: `linear-gradient(90deg, ${palette.primary}, ${palette.accent}, ${palette.secondary})`,
            zIndex: 2,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={4} justifyContent="center" textAlign="center">
            {[
              { num: t('trust.stat_1_num'), label: t('trust.stat_1_label') },
              { num: t('trust.stat_2_num'), label: t('trust.stat_2_label') },
              { num: t('trust.stat_3_num'), label: t('trust.stat_3_label') },
              { num: t('trust.stat_4_num'), label: t('trust.stat_4_label') },
            ].map((s, idx) => (
              <Grid item xs={6} md={3} key={s.label}>
                <Box
                  sx={{
                    opacity: trustVis ? 1 : 0,
                    transform: trustVis ? 'translateY(0)' : 'translateY(20px)',
                    transition: `all 0.5s ease ${idx * 0.1}s`,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 950,
                      fontSize: { xs: '1.6rem', md: '2.2rem' },
                      background: `linear-gradient(135deg, ${palette.accent}, #f0d890)`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {s.num}
                  </Typography>
                  {/* Short decorative line */}
                  <Box sx={{ width: 24, height: 2, bgcolor: `${palette.accent}55`, borderRadius: 1, mx: 'auto', my: 0.8 }} />
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    {s.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── FAQ ── */}
      <Box ref={faqRef} sx={{ bgcolor: palette.sectionAlt, py: { xs: 7, md: 9 }, ...paperTextureSx }}>
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h3" sx={{ fontWeight: 950, color: palette.textDark }}>
              {t('faq.title')}
            </Typography>
            <Box sx={{ width: 48, height: 3, bgcolor: palette.accent, borderRadius: 2, mx: 'auto', mt: 1 }} />
          </Box>
          {faqs.map((f, idx) => (
            <Accordion
              key={f.q}
              elevation={0}
              sx={{
                mb: 2,
                bgcolor: palette.bgCard,
                border: `1px solid ${palette.border}`,
                borderRadius: '12px !important',
                '&:before': { display: 'none' },
                transition: 'all 0.25s ease',
                opacity: faqVis ? 1 : 0,
                transform: faqVis ? 'translateY(0)' : 'translateY(20px)',
                transitionDelay: `${idx * 0.08}s`,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: palette.accent }} />}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  {/* Numbered circle */}
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: `${palette.accent}18`,
                      color: palette.accent,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 900,
                      fontSize: '0.8rem',
                      flexShrink: 0,
                    }}
                  >
                    {String(idx + 1).padStart(2, '0')}
                  </Box>
                  <Typography sx={{ fontWeight: 900, color: palette.textDark }}>
                    {f.q}
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ pl: 6.5 }}>
                <Box sx={{ borderLeft: `3px solid ${palette.accent}33`, pl: 2 }}>
                  <Typography sx={{ color: palette.textMuted, lineHeight: 1.8 }}>
                    {f.a}
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* ── Final CTA ── */}
      <Box
        sx={{
          bgcolor: palette.bgDark,
          color: 'white',
          py: { xs: 7, md: 9 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          backgroundImage: 'url(/images/landing/cta-background.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Dark overlay */}
        <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(15,30,46,0.88)' }} />
        {/* Floating decorative dots */}
        {[
          { top: '15%', left: '10%', size: 8, delay: '0s' },
          { top: '70%', left: '85%', size: 6, delay: '1s' },
          { top: '30%', right: '15%', size: 10, delay: '2s' },
          { bottom: '20%', left: '20%', size: 5, delay: '3s' },
        ].map((dot, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              ...dot,
              width: dot.size,
              height: dot.size,
              borderRadius: '50%',
              bgcolor: palette.accent,
              opacity: 0.3,
              animation: `dotFloat 4s ease-in-out ${dot.delay} infinite`,
            }}
          />
        ))}
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
          <Typography variant="h3" sx={{ fontWeight: 950, mb: 1 }}>
            {t('cta_section.title')}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.85, fontWeight: 400, mb: 4 }}>
            {t('cta_section.subtitle')}
          </Typography>
          <Button
            size="large"
            variant="contained"
            onClick={() => handleGetStarted('free')}
            endIcon={<RocketIcon />}
            sx={{
              bgcolor: palette.accent,
              color: palette.bgDark,
              px: 6,
              py: 1.9,
              borderRadius: 2,
              fontWeight: 950,
              boxShadow: '0 10px 30px rgba(214,167,91,0.25)',
              animation: 'pulse 2.5s ease-in-out infinite',
              '&:hover': { bgcolor: '#c4963e', boxShadow: '0 14px 36px rgba(214,167,91,0.28)' },
            }}
          >
            {t('cta_section.button')}
          </Button>
        </Container>
      </Box>

      {/* ── Footer ── */}
      <Box
        sx={{
          bgcolor: palette.bgDark,
          color: 'white',
          py: 5,
          position: 'relative',
          // Top gradient gold line
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg, ${palette.primary}, ${palette.accent}, ${palette.secondary})`,
          },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Typography sx={{ fontWeight: 950, letterSpacing: '-0.02em' }}>URStore</Typography>
              <Typography variant="body2" sx={{ opacity: 0.6, mt: 1, lineHeight: 1.8 }}>
                {t('landing_footer.tagline')}
              </Typography>
            </Grid>
            <Grid item xs={6} md={2.5}>
              <Typography sx={{ fontWeight: 900, color: palette.textLight, mb: 1 }}>{t('landing_footer.links')}</Typography>
              <Stack spacing={1}>
                {[
                  { label: t('landing_footer.features', { defaultValue: '功能介紹' }), to: '/features' },
                  { label: t('landing_footer.help_center', { defaultValue: '幫助中心' }), to: '/help' },
                  { label: t('landing_footer.contact_us', { defaultValue: '聯繫我們' }), to: '/contact' },
                ].map((link) => (
                  <Typography
                    key={link.label}
                    variant="body2"
                    sx={{
                      opacity: 0.55,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': { opacity: 1, color: palette.accent },
                    }}
                    onClick={() => navigate(link.to)}
                  >
                    {link.label}
                  </Typography>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={6} md={2.5}>
              <Typography sx={{ fontWeight: 900, color: palette.textLight, mb: 1 }}>{t('landing_footer.legal')}</Typography>
              <Stack spacing={1}>
                {[
                  { label: t('landing_footer.terms', { defaultValue: '服務條款' }), to: '/terms' },
                  { label: t('landing_footer.privacy', { defaultValue: '隱私政策' }), to: '/privacy' },
                ].map((link) => (
                  <Typography
                    key={link.label}
                    variant="body2"
                    sx={{
                      opacity: 0.55,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': { opacity: 1, color: palette.accent },
                    }}
                    onClick={() => navigate(link.to)}
                  >
                    {link.label}
                  </Typography>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography sx={{ fontWeight: 900, color: palette.textLight, mb: 1 }}>{t('landing_footer.start')}</Typography>
              <Button
                fullWidth
                variant="contained"
                onClick={() => handleGetStarted('free')}
                sx={{ bgcolor: palette.accent, color: palette.bgDark, fontWeight: 950, borderRadius: 2, '&:hover': { bgcolor: '#c4963e' } }}
              >
                {t('landing_nav.free_trial')}
              </Button>
            </Grid>
          </Grid>
          <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.06)' }} />
          <Typography variant="body2" sx={{ textAlign: 'center', opacity: 0.35 }}>
            Copyright © 2026 URStore. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}

export default Landing;
